import { LinkAIScribeUserResponse } from "@/AIScribe/Api/AIScribeUser.dto";
import { AsyncAction } from "@/Common/Api/AsyncActions/AsyncAction";
import { GenericResponse } from "@/Common/Api/Dto/GenericResponse";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { CreateNewNoteResponseModel } from "../Models/CreateNewNoteResponseModel";
import AppendEncounterProgressNoteFieldsResponse from "./Responses/AppendEncounterProgressNoteFieldsResponse";
import { AIScribeConsultationNote, StartAIScribeConsultationResponse } from "./AIScribeConsultation.dto";

class AIScribeAction extends AsyncAction {
    public async linkUserAsync(email: string, password: string): Promise<LinkAIScribeUserResponse> {
        return this.postAsync<LinkAIScribeUserResponse>(
            ServerOperations.Server.Myle_AIScribe_LinkUser,
            {
                email,
                password
            }
        );
    }

    public async synchronizeAsync(consultationId: string): Promise<AIScribeConsultationNote> {
        return this.postAsync<AIScribeConsultationNote>(
            ServerOperations.Server.Myle_AIScribe_SynchronizeConsultationNote,
            {
                AIScribeConsultationId: consultationId
            }
        );
    }

    public async startAsync(encounterId: string): Promise<StartAIScribeConsultationResponse> {
        return this.postAsync<StartAIScribeConsultationResponse>(
            ServerOperations.Server.Myle_AIScribe_StartConsultation,
            {
                EncounterId: encounterId
            }
        );
    }

    public async updateConsultationRecordingDuration(
        consultationId: string,
        recordingDurationInMilliSeconds: number
    ): Promise<void> {
        return this.postAsync(
            ServerOperations.Server.Myle_AIScribe_UpdateConsultationRecordingDuration,
            {
                AIScribeConsultationId: consultationId,
                RecordingDurationInMilliSeconds: recordingDurationInMilliSeconds
            }
        );
    }

    public async updateConsultationGenerationFailureReason(
        consultationId: string,
        errorReason: string
    ): Promise<void> {
        return this.postAsync(
            ServerOperations.Server.Myle_AIScribe_UpdateConsultationGenerationFailureReason,
            {
                AIScribeConsultationId: consultationId,
                FailureReason: errorReason
            }
        );
    }

    public async createNewNote(
        patientContactFacetId: string,
        aiScribeConsultationId: string,
        {
            VisitPurpose,
            HistoricalNote,
            ExamenNote,
            ProgressNote,
            OtherPlanNote
        }: Readonly<Encounter>
    ): Promise<CreateNewNoteResponseModel> {
        const currentSiteId = sessionStore.getCurrentEmployee().getActiveSite().Model.Id;
        const costCenterId = PatientHelper.getCostCenterId();
        const defaultSpecialization = sessionStore.getCurrentEmployeeDefaultSpecializationLookupId();
        const contextLookupId = cacheStore.FindLookupByLookupCode("Clinic Visite", "CONSULTATIONCONTEXT").LookupID;
        const requestData = {
            PatientContactFacetId: patientContactFacetId,
            SiteId: currentSiteId,
            ConsultationTypeId: defaultSpecialization,
            ConsultationContextId: contextLookupId,
            CostCenterId: costCenterId,
            AIScribeConsultationId: aiScribeConsultationId,
            VisitPurpose: VisitPurpose,
            ExamenNote: ExamenNote,
            OtherPlanNote: OtherPlanNote,
            HistoricalNote: HistoricalNote,
            ProgressNote: ProgressNote
        };

        return this.postAsync<CreateNewNoteResponseModel>(ServerOperations.Server.Myle_Patient_MedicalNote_CreateEncounter, requestData);
    }

    public async appendEnounterProgressNote(
        patientContactId: string,
        aIScribeConsultationId: string,
        {
            Id,
            VisitPurpose,
            HistoricalNote,
            ExamenNote,
            RadiologyNote,
            LabNote,
            ProgressNote,
            OtherPlanNote
        }: Readonly<Encounter>,
        forceOverride: boolean = false
    ): Promise<AppendEncounterProgressNoteFieldsResponse> {
        const requestData = {
            PatientContactId: patientContactId,
            AIScribeConsultationId: aIScribeConsultationId,
            OverrideNoteText: forceOverride,
            EncounterId: Id,
            VisitPurpose: VisitPurpose,
            HistoricalNote: HistoricalNote,
            ExamenNote: ExamenNote,
            RadiologyNote: RadiologyNote,
            LabNote: LabNote,
            OtherPlanNote: OtherPlanNote,
            ProgressNote: ProgressNote
        };

        return this.postAsync<AppendEncounterProgressNoteFieldsResponse>(ServerOperations.Server.Myle_Patient_MedicalNote_AppendEncounterProgressNoteFields, requestData);
    }

    public async LogMarketingPopupDisplayAsync(): Promise<void> {
        await this.postAsync(ServerOperations.Server.Myle_AIScribe_LogMarketingPopupDisplay, {});
    }

    public async unlinkUserAsync(): Promise<GenericResponse> {
        return this.postAsync(ServerOperations.Server.Myle_AIScribe_UnlinkUser, {});
    }
}

export default AIScribeAction;
